<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { resolveDownload, type DownloadResolution, type MobilePlatform } from '@/api/downloads'
import { detectDownloadDevice } from '@/utils/downloadDevice'
import { useWebsiteSettings } from '@/composables/useWebsiteSettings'
import { PhArrowUpRight, PhCircleNotch, PhDeviceMobile, PhInfo } from '@phosphor-icons/vue'
import prismBackground from '@/assets/mobile-download-prism.png'

const { settings } = useWebsiteSettings()
const platform = ref<MobilePlatform>('UNKNOWN')
const brand = ref('UNKNOWN')
const wechat = ref(false)
const result = ref<DownloadResolution | null>(null)
const loading = ref(true)
const error = ref(false)
const attempted = ref(false)

const platformName = computed(
  () =>
    ({ IOS: 'iOS / iPadOS', ANDROID: 'Android', HARMONYOS: '原生鸿蒙', UNKNOWN: '你的设备' })[
      platform.value
    ],
)

let sequence = 0
const ready = computed(
  () =>
    !loading.value && !error.value && !result.value?.choosePlatform && !result.value?.openInBrowser,
)
const download = computed(() =>
  ready.value && result.value?.recommended?.available ? result.value.recommended.url : null,
)

const statusText = computed(() => {
  if (wechat.value || result.value?.openInBrowser) return '请在浏览器中打开'
  if (loading.value) return '正在获取下载渠道…'
  if (error.value) return '下载信息暂时无法获取'
  if (platform.value === 'UNKNOWN' || result.value?.choosePlatform) return '请使用手机浏览器打开'
  if (!download.value) return '该平台暂未开放下载'
  return attempted.value ? '已尝试打开下载渠道' : '正在前往下载渠道…'
})
const busy = computed(() => !wechat.value && loading.value)

async function refresh() {
  const id = ++sequence
  const detected = detectDownloadDevice(window.navigator.userAgent, window.navigator.maxTouchPoints)
  wechat.value = detected.environment === 'WECHAT'

  platform.value = detected.platform
  brand.value = detected.brand
  loading.value = true
  error.value = false
  result.value = null
  try {
    const response = await resolveDownload({
      platform: platform.value,
      brand: platform.value === 'ANDROID' ? brand.value : 'UNKNOWN',
      environment: detected.environment,
    })
    if (id === sequence) result.value = response
  } catch {
    if (id === sequence) error.value = true
  } finally {
    if (id === sequence) {
      loading.value = false
      if (!wechat.value && platform.value !== 'UNKNOWN' && download.value) {
        // Only once per tab/platform, including a Back navigation from a store.
        const key = `maizhi-download-attempt:${platform.value}`
        try {
          if (!window.sessionStorage.getItem(key)) {
            window.sessionStorage.setItem(key, '1')
            window.location.assign(download.value)
          }
          attempted.value = true
        } catch {
          error.value = true
        }
      }
    }
  }
}

function resume() {
  if (document.visibilityState === 'visible') void refresh()
}
onMounted(() => {
  void refresh()
  window.addEventListener('pageshow', resume)
  document.addEventListener('visibilitychange', resume)
})
onBeforeUnmount(() => {
  sequence++
  window.removeEventListener('pageshow', resume)
  document.removeEventListener('visibilitychange', resume)
})
</script>

<template>
  <main class="mobile-download-page">
    <div class="download-scene">
      <img
        class="download-backdrop"
        :src="prismBackground"
        alt=""
        aria-hidden="true"
        fetchpriority="high"
      />
      <div class="download-content">
        <aside v-if="wechat || result?.openInBrowser" class="wechat-guide" role="note">
          <PhArrowUpRight :size="30" aria-hidden="true" />
          <strong>在浏览器中打开</strong>
          <span>点击右上角 ···，选择“在浏览器中打开”</span>
        </aside>
        <header class="download-brand">
          <img v-if="settings.logoUrl" :src="settings.logoUrl" alt="麦汁实验室" />
          <span v-else>麦汁实验室</span>
        </header>
        <h1>让科学看得见，<br />让探索更简单。</h1>
        <section class="download-status" aria-live="polite" aria-atomic="true">
          <PhCircleNotch
            v-if="busy"
            class="status-icon is-spinning"
            :size="34"
            aria-hidden="true"
          />
          <PhDeviceMobile
            v-else-if="wechat || platform === 'UNKNOWN'"
            class="status-icon"
            :size="32"
            aria-hidden="true"
          />
          <PhInfo v-else class="status-icon" :size="32" aria-hidden="true" />
          <div>
            <p class="status-title">{{ statusText }}</p>
            <p v-if="platform !== 'UNKNOWN'" class="status-detail">已识别 {{ platformName }}</p>
            <p v-else class="status-detail">移动端 App 下载</p>
          </div>
        </section>
        <footer>mzhi.net</footer>
      </div>
    </div>
  </main>
</template>

<style scoped>
.mobile-download-page {
  min-height: 100svh;
  background: #e7f3ff;
  color: var(--navy);
}
.download-scene {
  position: relative;
  isolation: isolate;
  min-height: 100svh;
  max-width: 560px;
  margin: auto;
  overflow: hidden;
}
.download-backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: -1;
}
.download-content {
  min-height: max(720px, 100svh);
  display: flex;
  flex-direction: column;
  padding: max(52px, env(safe-area-inset-top)) 28px max(34px, env(safe-area-inset-bottom));
}
.download-brand {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.download-brand img {
  display: block;
  width: 188px;
  height: 44px;
  object-fit: contain;
  object-position: center;
}
.download-brand span {
  font-size: 22px;
  font-weight: 600;
}
h1 {
  margin: 42px 0 0;
  font-size: clamp(28px, 8vw, 36px);
  line-height: 1.5;
  letter-spacing: -0.5px;
  font-weight: 650;
}
.download-status {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 78px;
  padding: 17px 20px;
  background: rgba(235, 246, 255, 0.73);
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 12px;
  backdrop-filter: blur(14px);
}
.status-icon {
  flex: none;
  color: #0878ec;
}
.status-title {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  font-weight: 500;
}
.status-detail {
  margin: 5px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #365a83;
}
footer {
  margin-top: 58px;
  text-align: center;
  font-size: 13px;
  letter-spacing: 2px;
  color: #edf7ff;
}
.wechat-guide {
  position: relative;
  margin: -24px -10px 22px;
  padding: 14px 38px 14px 14px;
  border: 1px solid #ffffffa8;
  border-radius: 10px;
  background: #eff8fff0;
}
.wechat-guide svg {
  position: absolute;
  right: 8px;
  top: 7px;
  color: var(--blue);
}
.wechat-guide strong {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 500;
}
.wechat-guide span {
  font-size: 11px;
  line-height: 1.6;
  color: #365a83;
}
.is-spinning {
  animation: spin 1.2s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .is-spinning {
    animation: none;
  }
}
@media (max-width: 360px) {
  .download-content {
    padding-left: 22px;
    padding-right: 22px;
  }
  .download-status {
    gap: 12px;
    padding: 16px;
  }
}
@media (min-width: 601px) {
  .download-content {
    min-height: max(844px, 100svh);
    padding-left: 36px;
    padding-right: 36px;
  }
}
</style>
